/*
 * Native wishlist — replaces the "Wishlist by Square" app.
 *
 * The list lives in localStorage, so it is per browser: it does not sync
 * across devices and is lost if the visitor clears their site data. That
 * trade-off was accepted deliberately — writing to customer metafields from
 * the storefront is not possible without a backend.
 *
 * Loaded with defer, so hearts and the header badge settle into their saved
 * state just after the HTML is parsed — a brief moment where everything looks
 * "not saved" is possible on a slow first load.
 *
 * Clicks are handled by one delegated listener and state is written as an
 * attribute on the custom element itself (never on child nodes). That keeps
 * buttons working when they enter the DOM later — AJAX collection filtering,
 * the quick view drawer, cart recommendations — with no re-initialisation.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'king-wishlist';
  var CHANGE_EVENT = 'wishlist:change';

  function readStorage() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map(function (value) {
          return String(value);
        })
        .filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  var items = readStorage();

  function writeStorage() {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* private mode or quota exceeded — the list still works for this session */
    }
  }

  function broadcast() {
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: { items: items.slice(), count: items.length },
      })
    );
  }

  var Wishlist = {
    items: function () {
      return items.slice();
    },
    count: function () {
      return items.length;
    },
    has: function (id) {
      return items.indexOf(String(id)) !== -1;
    },
    add: function (id) {
      id = String(id);
      if (!id || Wishlist.has(id)) return false;
      items.push(id);
      writeStorage();
      broadcast();
      return true;
    },
    remove: function (id) {
      var index = items.indexOf(String(id));
      if (index === -1) return false;
      items.splice(index, 1);
      writeStorage();
      broadcast();
      return true;
    },
    toggle: function (id) {
      return Wishlist.has(id) ? (Wishlist.remove(id), false) : (Wishlist.add(id), true);
    },
    clear: function () {
      if (!items.length) return;
      items = [];
      writeStorage();
      broadcast();
    },
  };

  window.Wishlist = Wishlist;

  /* another tab changed the list */
  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;
    items = readStorage();
    broadcast();
  });

  /*
   * One delegated listener instead of per-button handlers, so buttons work
   * regardless of when they enter the DOM (AJAX collection filtering, quick
   * view drawer, cart drawer recommendations…).
   */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var host = target.closest('wishlist-button');
    if (!host) return;
    var id = host.getAttribute('data-product-id');
    if (!id) return;
    event.preventDefault();
    Wishlist.toggle(id);
  });

  function define(name, constructor) {
    if (!window.customElements || window.customElements.get(name)) return;
    window.customElements.define(name, constructor);
  }

  /* ---------------------------------------------------------------- button */

  define(
    'wishlist-button',
    class extends HTMLElement {
      connectedCallback() {
        this.onWishlistChange = this.sync.bind(this);
        document.addEventListener(CHANGE_EVENT, this.onWishlistChange);
        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener(CHANGE_EVENT, this.onWishlistChange);
      }

      sync() {
        var saved = Wishlist.has(this.getAttribute('data-product-id'));
        this.setAttribute('data-saved', saved ? 'true' : 'false');

        /*
         * The inner button may not be parsed yet on the first synchronous
         * call; that is fine, the visual state above is already correct and
         * this runs again on every change event.
         */
        var button = this.querySelector('button');
        if (!button) return;
        button.setAttribute('aria-pressed', saved ? 'true' : 'false');
        var label = saved
          ? button.getAttribute('data-wishlist-label-remove')
          : button.getAttribute('data-wishlist-label-add');
        if (label) button.setAttribute('aria-label', label);
      }
    }
  );

  /* ----------------------------------------------------------------- count */

  define(
    'wishlist-count',
    class extends HTMLElement {
      connectedCallback() {
        this.onWishlistChange = this.sync.bind(this);
        document.addEventListener(CHANGE_EVENT, this.onWishlistChange);
        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener(CHANGE_EVENT, this.onWishlistChange);
      }

      sync() {
        var count = Wishlist.count();
        this.textContent = count > 99 ? '99+' : String(count);
        this.toggleAttribute('hidden', count === 0);
      }
    }
  );

  /* ------------------------------------------------------------ list (page) */

  define(
    'wishlist-list',
    class extends HTMLElement {
      connectedCallback() {
        this.onWishlistChange = this.sync.bind(this);
        document.addEventListener(CHANGE_EVENT, this.onWishlistChange);

        /* children are not parsed yet during the synchronous upgrade */
        if (document.readyState === 'loading') {
          this.onReady = this.sync.bind(this);
          document.addEventListener('DOMContentLoaded', this.onReady);
        }
        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener(CHANGE_EVENT, this.onWishlistChange);
        if (this.onReady) document.removeEventListener('DOMContentLoaded', this.onReady);
      }

      sync() {
        var shown = 0;
        var entries = this.querySelectorAll('[data-wishlist-item]');

        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          var saved = Wishlist.has(entry.getAttribute('data-product-id'));
          entry.toggleAttribute('hidden', !saved);
          if (saved) shown += 1;
        }

        this.setAttribute('data-state', shown ? 'filled' : 'empty');

        /*
         * Saved products that are no longer rendered (unpublished, deleted, or
         * outside the rendered window) would otherwise keep the page looking
         * empty while the header badge shows a count. Report the mismatch so
         * the section can decide what to show.
         */
        this.setAttribute('data-shown', String(shown));
      }
    }
  );

  /* clear-all button on the wishlist page */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-wishlist-clear]')) return;
    event.preventDefault();
    Wishlist.clear();
  });
})();
